import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Key, Trash2, ExternalLink } from "lucide-react";
import axiosInstance from "@/config/axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ApifySettings() {
  const [apiKey, setApiKey] = useState("");
  const [loading, setLoading] = useState(false);
  const [hasExistingKey, setHasExistingKey] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axiosInstance.get(`${API}/api/admin/settings/apify`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.api_key) {
        setApiKey(response.data.api_key);
        setHasExistingKey(true);
      }
    } catch (error) {
      console.error("Error fetching Apify settings:", error);
    }
  };

  const handleSave = async () => {
    if (!apiKey.trim()) {
      toast.error("Please enter an API key");
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      await axiosInstance.post(
        `${API}/api/admin/settings/apify`,
        { api_key: apiKey },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Apify API key saved successfully!");
      setHasExistingKey(true);
    } catch (error) {
      console.error("Error saving API key:", error);
      toast.error(error.response?.data?.detail || "Failed to save API key");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete the Apify API key?")) {
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      await axiosInstance.delete(`${API}/api/admin/settings/apify`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setApiKey("");
      setHasExistingKey(false);
      toast.success("Apify API key deleted successfully!");
    } catch (error) {
      console.error("Error deleting API key:", error);
      toast.error(error.response?.data?.detail || "Failed to delete API key");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-8">
      <div className="max-w-4xl mx-auto space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-800">Apify Settings</h1>
          <p className="text-slate-600 mt-2">
            Configure your Apify API key to access web scraping actors
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-blue-600" />
              Apify API Key
            </CardTitle>
            <CardDescription>
              Your API key is encrypted and stored securely. Get your API key from{" "}
              <a
                href="https://console.apify.com/account/integrations"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline inline-flex items-center gap-1"
              >
                Apify Console
                <ExternalLink className="w-3 h-3" />
              </a>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="api_key">API Key</Label>
              <Input
                id="api_key"
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="apify_api_..."
                className="mt-1"
              />
            </div>

            <div className="flex gap-3">
              <Button onClick={handleSave} disabled={loading}>
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Key className="w-4 h-4 mr-2" />
                    Save API Key
                  </>
                )}
              </Button>

              {hasExistingKey && (
                <Button
                  variant="destructive"
                  onClick={handleDelete}
                  disabled={loading}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="border-blue-200 bg-blue-50">
          <CardHeader>
            <CardTitle className="text-blue-800">About Apify</CardTitle>
          </CardHeader>
          <CardContent className="text-blue-700 space-y-2">
            <p>
              <strong>Apify</strong> is a web scraping and automation platform with 1,500+ ready-made actors (scrapers).
            </p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>Instagram scraper, Amazon scraper, Google Maps scraper, and more</li>
              <li>Run actors with custom inputs</li>
              <li>Get structured data (JSON, CSV, Excel)</li>
              <li>Reliable and scalable infrastructure</li>
            </ul>
            <p className="mt-3">
              <a
                href="https://docs.apify.com/api/v2"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline inline-flex items-center gap-1 font-medium"
              >
                View Apify API Documentation
                <ExternalLink className="w-4 h-4" />
              </a>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
